import React from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";

const scrollbarStyles = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "4px",
    backgroundColor: "#FAFAFF",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#E7E9EC",
    borderRadius: "4px",
  },
};

export const listStyles = {
  list: {
    ...scrollbarStyles,
  },
  listHover: {
    "&::-webkit-scrollbar": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
    },

    "&:hover": {
      ...scrollbarStyles,
    },
  },
};

const useStyles = makeStyles({ ...listStyles });

export interface ListRecordResponse<T> {
  items: T[];
  records: number;
}

class InfiniteListState<T> {
  items: T[];

  loaded: boolean;

  page: number;

  constructor(items: T[]) {
    this.items = items;
    this.loaded = false;
    this.page = 0;
  }

  reset() {
    this.items = [];
    this.loaded = false;
    this.page = 0;
  }
}

export interface DynamicInfiniteListProps {
  renderItem(index: number, data: unknown): React.ReactNode;
  loadItems(page: number): Promise<ListRecordResponse<unknown>>;
  LoadingPlaceholder?: React.ReactNode;
  EmptyPlaceholder?: React.ComponentType;
  className?: string;
  resetItems?: boolean;
  scrollOnHover?: boolean;
  scrollToIndex?: number;
}

const DynamicInfiniteList = ({
  className,
  loadItems,
  renderItem,
  resetItems,
  scrollOnHover,
  EmptyPlaceholder,
  LoadingPlaceholder,
  scrollToIndex,
}: DynamicInfiniteListProps): JSX.Element => {
  const classes = useStyles();
  const [state, updateState] = React.useState(new InfiniteListState<any>([]));
  const [loading, setLoading] = React.useState<boolean>(false);
  const virtuoso = React.useRef<VirtuosoHandle>(null);

  const loadMoreItems = () => {
    if (state.loaded) return;
    const page = state.page + 1;

    if (page === 1) {
      setLoading(true);
    }

    loadItems(page)
      .then((response: ListRecordResponse<unknown>) => {
        setLoading(false);
        state.items.push(...response.items);
        const newState = new InfiniteListState(state.items);
        newState.page = page;
        newState.loaded = state.items.length >= response.records;
        updateState(newState);
      })
      .catch(() => {
        setLoading(false);
        const newState = new InfiniteListState(state.items);
        newState.loaded = true;
        updateState(newState);
      });
  };

  React.useEffect(() => {
    if (scrollToIndex !== undefined && scrollToIndex >= 0 && scrollToIndex < state.items.length) {
      virtuoso.current?.scrollToIndex({ index: scrollToIndex, align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  React.useEffect(() => {
    if (!resetItems) loadMoreItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (resetItems) {
      state.reset();
      loadMoreItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetItems]);

  if (loading && LoadingPlaceholder) return <>{LoadingPlaceholder}</>;

  return (
    <Virtuoso
      className={clsx(className, classes.list, scrollOnHover && classes.listHover)}
      totalCount={state.items.length}
      data={state.items}
      endReached={loadMoreItems}
      itemContent={renderItem}
      components={{
        EmptyPlaceholder,
      }}
      ref={virtuoso}
    />
  );
};

export default DynamicInfiniteList;
